import { useEffect, useRef } from 'react';
import type { Map } from 'leaflet';

interface LocationMiniMapProps {
  latitude: number;
  longitude: number;
  machineLat?: number;
  machineLng?: number;
  geofenceRadius?: number;
  height?: string;
  className?: string;
}

export function LocationMiniMap({
  latitude,
  longitude,
  machineLat,
  machineLng,
  geofenceRadius,
  height = '180px',
  className,
}: LocationMiniMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const loadMap = async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');

      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
      });

      if (!mapRef.current) return;

      const hasMachine = machineLat != null && machineLng != null;

      const map = L.map(mapRef.current, {
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        touchZoom: false,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(map);

      const userMarker = L.marker([latitude, longitude]).addTo(map);
      userMarker.bindTooltip('Korisnik', { direction: 'top', offset: [0, -35] });

      if (hasMachine) {
        const machineIcon = L.divIcon({
          html: `<div style="width:28px;height:28px;background:#16a34a;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><line x1="6" y1="6" x2="6" y2="6.01"/><line x1="10" y1="6" x2="10" y2="6.01"/></svg>
          </div>`,
          className: '',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        });

        L.marker([machineLat!, machineLng!], { icon: machineIcon }).addTo(map)
          .bindTooltip('Aparat', { direction: 'top', offset: [0, -16] });

        if (geofenceRadius && geofenceRadius > 0) {
          L.circle([machineLat!, machineLng!], {
            radius: geofenceRadius,
            color: '#16a34a',
            fillColor: '#16a34a',
            fillOpacity: 0.08,
            weight: 2,
            dashArray: '6 4',
          }).addTo(map);
        }

        const bounds = L.latLngBounds([
          [latitude, longitude],
          [machineLat!, machineLng!],
        ]);
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
      } else {
        map.setView([latitude, longitude], 16);
      }

      mapInstanceRef.current = map;
    };

    loadMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [latitude, longitude, machineLat, machineLng, geofenceRadius]);

  return (
    <div
      ref={mapRef}
      className={className}
      style={{ height, width: '100%', borderRadius: '8px', overflow: 'hidden' }}
    />
  );
}
